// The one shared "fixed-width flex column" panel wrapper and section label
// used across every editor mode's side panels (Systems modes, Pixel Studio,
// and Edit mode's own scene/inspector panels). Kept as its own leaf module
// with no other editor-internal imports: editor-modes2.tsx, screens-editor.tsx
// and editor-pixel.tsx all need it, and each other transitively (via
// EditorHost.tsx), so defining it in any one of them creates a circular
// import the moment another one imports it back.
import { useState, type CSSProperties, type ReactNode } from 'react';

/**
 * The two dock-column widths every workspace side panel uses. Before these
 * existed each panel picked its own number by eye - 220/232/252/260/264/290
 * were all in use simultaneously, so two panels sitting side by side in the
 * same workspace (Scene's Environment presets at 232 next to Edit's scene
 * tree at 252) never lined up with each other or with the dockview zone
 * widths in panel-system/Workspace.tsx. Left columns hold structure (a
 * scene tree, a floor list, a preset list) and need less room than right
 * columns, which hold labelled property rows - notably the three-across
 * X/Y/Z transform fields, which set the right column's floor.
 */
export const PANEL_W_LEFT = 264;
export const PANEL_W_RIGHT = 288;

/**
 * A fixed-width, independently scrolling dock column.
 *
 * `paddingBottom` is deliberately small: the old value (64px) was a blanket
 * dead strip under every panel's last control, which read as an unexplained
 * gap on the many panels whose content does not fill the column. Panels
 * that genuinely want their last action pinned to the bottom (the
 * inspectors' "Delete object" buttons) already do that with `marginTop:
 * auto`, which works correctly on a flex column and does not need reserved
 * space to sit in.
 */
export function M2Panel({ side, width, children, style }: { side: 'left' | 'right'; width?: number; children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="ed-panel" style={{
      width: width ?? (side === 'left' ? PANEL_W_LEFT : PANEL_W_RIGHT),
      flexShrink: 0, overflowY: 'auto', background: 'var(--ss-bg-2)', paddingBottom: 16,
      borderRight: side === 'left' ? '1px solid var(--ss-border-soft)' : 'none',
      borderLeft: side === 'right' ? '1px solid var(--ss-border-soft)' : 'none',
      display: 'flex', flexDirection: 'column', minHeight: 0,
      ...style,
    }}>{children}</div>
  );
}

/**
 * A panel's section heading. The `ed-panel-sec` class carries the top
 * margin so `.ed-panel > .ed-panel-sec:first-child` (editor-theme.css) can
 * drop it for a panel's *first* section - a fixed 20px top margin on every
 * label left every panel's first heading floating 20px below the dock tab
 * strip while the panel beside it, whose first child happened to be a
 * search field, started flush.
 */
export function M2Label({ children, action, style }: { children: ReactNode; action?: ReactNode; style?: CSSProperties }) {
  return (
    <div className="ed-panel-sec" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '0 16px', marginBottom: 10, ...style }}>
      <span style={{ fontWeight: 600, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ss-text-3)', whiteSpace: 'nowrap' }}>{children}</span>
      {action}
    </div>
  );
}

/**
 * The labelled-control row every side panel's `<select>`/`<input>` pairs
 * were hand-rolling - a `<span>` caption over a full-width control, with
 * the caption's own size/colour repeated inline at ~20 call sites across
 * Edit's five inspectors, Map Builder and Environment. One definition
 * instead, so a caption's metrics are set in one place.
 */
export function EdField({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }} title={hint}>
      <span style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)' }}>{label}</span>
      {children}
    </label>
  );
}

/** `EdField` wrapping the app's standard `<select>` - the single most
 * repeated pair of the two (Kind / Team / Material / Bomb site / Colour
 * grade / Weather all had a byte-identical copy). */
export function EdSelect({ label, hint, value, onChange, children }: {
  label: string; hint?: string; value: string; onChange: (value: string) => void; children: ReactNode;
}) {
  return (
    <EdField label={label} hint={hint}>
      <select className="ss-input ed-select" value={value} onChange={(e) => onChange(e.target.value)}>{children}</select>
    </EdField>
  );
}

/**
 * The destructive action pinned to the bottom of an inspector. Five
 * byte-identical copies of this button (object / light / marker / trigger /
 * decal) lived in EditRoute, each repeating the same eight inline style
 * properties.
 */
export function EdDangerButton({ label, onClick, title }: { label: string; onClick: () => void; title?: string }) {
  return (
    <div style={{ padding: '8px 16px 16px', marginTop: 'auto' }}>
      <button className="ss-btn ss-btn--ghost ed-danger-btn" onClick={onClick} title={title}>{label}</button>
    </div>
  );
}

/**
 * A panel's "nothing to show yet" state. Every reference editor
 * (Godot's Inspector, Unity's Inspector, Unreal's Details) fills an empty
 * properties column with a centred, low-contrast explanation of what would
 * put something in it rather than leaving the column blank - previously
 * this was a single 40px-padded paragraph pinned to the top of an
 * otherwise empty 290px column.
 */
export function EdEmptyState({ icon, title, hint }: { icon?: ReactNode; title: string; hint?: string }) {
  return (
    <div style={{
      flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 8, padding: '24px 22px', textAlign: 'center',
    }}>
      {icon && <span style={{ color: 'var(--ss-text-3)', opacity: 0.6, marginBottom: 2 }}>{icon}</span>}
      <span style={{ fontSize: 'var(--ss-fs-sm)', fontWeight: 500, color: 'var(--ss-text-2)' }}>{title}</span>
      {hint && <span style={{ fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.6 }}>{hint}</span>}
    </div>
  );
}

export interface M2ListRowProps {
  /** Selected/current-item state: accent-soft background, accent label text. */
  active?: boolean;
  onClick?: () => void;
  onDoubleClick?: () => void;
  title?: string;
  /** Ellipsis-truncate the label to one line (default true). */
  truncate?: boolean;
  /**
   * False renders `children` directly in place of the label button - for a
   * row that swaps its label out for an inline rename `<input>`.
   */
  interactive?: boolean;
  children: ReactNode;
  /** Always-visible trailing controls the row doesn't otherwise style (a
   * toggle, reorder buttons, a warning badge) - rendered as given. */
  extra?: ReactNode;
  /** Trailing "x" delete control, dimmed until the row is hovered, matching
   * the .ss-row/.row-action reveal treatment (soulstack.css) other list rows
   * in the app already use. */
  onDelete?: () => void;
  deleteLabel?: string;
  style?: CSSProperties;
  /** Overrides for the label button itself (e.g. a site's own padding/font-size). */
  labelStyle?: CSSProperties;
}

/**
 * The "click a row to select it" list item used across every editor
 * sidebar's list panel (sprites, clips, data entries, materials, particles,
 * rules, widgets) - one shared shape for active/hover state instead of each
 * site hand-rolling its own (docs/editor-consolidation 2D-editor audit).
 */
export function M2ListRow({
  active, onClick, onDoubleClick, title, truncate = true, interactive = true,
  children, extra, onDelete, deleteLabel, style, labelStyle,
}: M2ListRowProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', borderRadius: 8,
        background: active ? 'var(--ss-accent-soft)' : hovered ? 'var(--ss-hover)' : 'transparent',
        transition: 'background 150ms ease',
        ...style,
      }}
    >
      {interactive ? (
        <button
          type="button" onClick={onClick} onDoubleClick={onDoubleClick} title={title}
          style={{
            flex: 1, minWidth: 0, textAlign: 'left', padding: '7px 12px', border: 'none', background: 'transparent',
            fontSize: 12.5, fontWeight: 450, color: active ? 'var(--ss-accent)' : 'var(--ss-text-2)',
            ...(truncate ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
            ...labelStyle,
          }}
        >{children}</button>
      ) : children}
      {extra}
      {onDelete && (
        <button
          type="button" onClick={onDelete} title={deleteLabel} aria-label={deleteLabel}
          style={{
            border: 'none', background: 'transparent', padding: '4px 9px', fontSize: 13, lineHeight: 1, color: 'var(--ss-text-3)', flexShrink: 0,
            opacity: hovered ? 1 : 0.5, transition: 'opacity 150ms ease',
          }}
        >×</button>
      )}
    </div>
  );
}

export interface EdSliderProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** Both inputs' step. Defaults to 1 (whole numbers); pass a fraction (e.g.
   * 0.5) where the underlying value supports finer precision than a
   * whole-unit drag reaches. */
  step?: number;
  /** Appended to the raw value for display (default '%'). Ignored when
   * `format` is given. */
  suffix?: string;
  /** Takes the raw value and returns its display text (e.g. "17:00" for a
   * time-of-day slider) - takes precedence over `suffix` when given. */
  format?: (value: number) => string;
  /** Tooltip on the label and track - for a value whose relevance or range
   * isn't self-evident (e.g. a 3D-only dimension shown on a 2D project, or
   * a non-0-100 multiplier scale), or explaining why it's disabled. */
  hint?: string;
  /** Dims the row and disables both inputs, for a control that isn't
   * available in the current context yet. */
  disabled?: boolean;
  /** Adds a paired numeric input for typing an exact value, clamped to
   * min/max. Off by default - only a slider that actually wants this should
   * grow it. */
  showExactValue?: boolean;
}

/**
 * The label-and-value row over a range track used across every editor
 * mode's side panels (Logic/HUD/Audio, Edit mode's object/light/decal
 * inspectors, Environment, Materials, FX) - one shared slider instead of
 * three near-identical copies (duplication audit, CHANGELOG.md
 * [Unreleased] "premium polish" entry). Always controlled: no real call
 * site across the merged set relied on an uncontrolled fallback.
 */
export function EdSlider({
  label, value, onChange, min = 0, max = 100, step = 1, suffix, format, hint, disabled = false, showExactValue = false,
}: EdSliderProps) {
  const display = format ? format(value) : `${value}${suffix ?? '%'}`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '0 16px', marginBottom: 14, opacity: disabled ? 0.45 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 'var(--ss-fs-xs)' }}>
        <span style={{ color: 'var(--ss-text-2)' }} title={hint}>{label}</span>
        <span style={{ fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>{display}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="range" min={min} max={max} step={step} value={value} disabled={disabled}
          onChange={(e) => onChange(+e.target.value)}
          className="ed-range" style={{ flex: 1 }} aria-label={label} title={hint}
        />
        {showExactValue && (
          <input
            className="ss-input"
            type="number" min={min} max={max} step={step} value={value} disabled={disabled}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, n)));
            }}
            aria-label={label + ' (exact value)'}
            style={{ width: 50, flexShrink: 0, padding: '3px 5px', fontSize: 'var(--ss-fs-xs)', borderRadius: 6, textAlign: 'right' }}
          />
        )}
      </div>
    </div>
  );
}
